import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import HistoryIcon from '@mui/icons-material/History';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckIcon from '@mui/icons-material/Check';
import CommitIcon from '@mui/icons-material/Commit';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIButton, SCUILoadingLinear, SCUITextField } from '../../atomic/atoms';
import RuntimeConfigurationQuery from '../../services/query/runtimeConfiguration/runtimeConfigurationQuery.services';
import RuntimeConfigurationCommand from '../../services/command/runtimeConfiguration/runtimeConfigurationCommand.services';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import {
    RuntimeConfigurationInputMode,
    RuntimeConfigurationValueType,
} from '../../types/enums/runtime-configuration-enums';
import {
    buildRuntimeConfigurationDetailUrl,
    getRuntimeConfigurationOperationMessage,
    getRuntimeConfigurationVersionFromSearch,
    isJsonValueType,
    isRuntimeConfigurationOperationFailed,
    JsonValidationResult,
    normalizeValueType,
    resolveRuntimeConfigurationMutationVersion,
    toPayloadValue,
} from './runtimeConfiguration.helpers';
import {
    GetRuntimeConfigurationResponse,
    RuntimeConfigurationVersionListItem,
} from './runtimeConfiguration.types';
import { RuntimeConfigurationValueEditor } from './components/RuntimeConfigurationValueEditor';
import { RuntimeConfigurationValueTypePicker } from './components/RuntimeConfigurationValueTypePicker';
import './components/RuntimeConfigurationValueEditor.scss';
import './runtime-configuration-pages.scss';

export const RuntimeConfigurationDetail: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { key: routeKey } = useParams<{ key: string }>();
    const configKey = decodeURIComponent(routeKey || '');

    const [config, setConfig] = useState<GetRuntimeConfigurationResponse | null>(null);
    const [versions, setVersions] = useState<RuntimeConfigurationVersionListItem[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<number | null>(() =>
        getRuntimeConfigurationVersionFromSearch(history.location.search)
    );
    const skipNextDetailFetchRef = useRef(false);
    const [editValue, setEditValue] = useState<unknown>(null);
    const [editValueType, setEditValueType] = useState<RuntimeConfigurationValueType>(
        RuntimeConfigurationValueType.String
    );
    const [changeNote, setChangeNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [jsonValidation, setJsonValidation] = useState<JsonValidationResult>({ valid: true, error: null });

    const liveCurrentVersion =
        versions.find((v) => v.isCurrent)?.version ??
        config?.currentVersion ??
        null;
    const viewingVersion = selectedVersion ?? liveCurrentVersion;
    const isViewingCurrentVersion =
        viewingVersion != null &&
        liveCurrentVersion != null &&
        viewingVersion === liveCurrentVersion;
    const readOnly = !isViewingCurrentVersion;
    const hasInvalidJson = isJsonValueType(editValueType) && !jsonValidation.valid;

    const syncUrlVersion = useCallback(
        (version: number) => {
            const nextUrl = buildRuntimeConfigurationDetailUrl(configKey, version);
            if (history.location.pathname + history.location.search !== nextUrl) {
                history.replace(nextUrl);
            }
        },
        [configKey, history]
    );

    const loadVersions = useCallback(async () => {
        if (!configKey) return;
        const versionList = await RuntimeConfigurationQuery.ListRuntimeConfigurationVersions(configKey);
        setVersions(Array.isArray(versionList) ? versionList : []);
    }, [configKey]);

    const fetchDetailByVersion = useCallback(
        async (version: number) => {
            if (!configKey) return;
            setLoading(true);
            try {
                const detail = await RuntimeConfigurationQuery.GetRuntimeConfiguration(configKey, version);
                setConfig(detail);
                if (detail.found) {
                    setEditValue(detail.value);
                    setEditValueType(normalizeValueType(detail.valueType));
                }
            } finally {
                setLoading(false);
            }
        },
        [configKey]
    );

    const navigateToVersion = useCallback(
        (version: number, options?: { skipFetch?: boolean }) => {
            if (options?.skipFetch) {
                skipNextDetailFetchRef.current = true;
            }
            setSelectedVersion(version);
            syncUrlVersion(version);
        },
        [syncUrlVersion]
    );

    useEffect(() => {
        if (!configKey) return;
        loadVersions();
    }, [configKey, loadVersions]);

    useEffect(() => {
        const urlVersion = getRuntimeConfigurationVersionFromSearch(history.location.search);
        setSelectedVersion((prev) => {
            if (urlVersion == null) {
                return prev === null ? prev : null;
            }
            return prev === urlVersion ? prev : urlVersion;
        });
    }, [history.location.search]);

    useEffect(() => {
        if (!configKey) return;

        if (selectedVersion != null) {
            if (skipNextDetailFetchRef.current) {
                skipNextDetailFetchRef.current = false;
                return;
            }
            fetchDetailByVersion(selectedVersion);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const detail = await RuntimeConfigurationQuery.GetRuntimeConfiguration(configKey);
                if (cancelled) return;

                setConfig(detail);
                if (!detail.found || detail.currentVersion == null) {
                    return;
                }

                setEditValue(detail.value);
                setEditValueType(normalizeValueType(detail.valueType));
                skipNextDetailFetchRef.current = true;
                navigateToVersion(detail.currentVersion, { skipFetch: true });
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [configKey, selectedVersion, fetchDetailByVersion, navigateToVersion]);

    const handleVersionChange = (next: string) => {
        navigateToVersion(Number(next));
    };

    const handleValueTypeChange = (next: RuntimeConfigurationValueType) => {
        setEditValueType(next);
    };

    const handleSave = async () => {
        if (!configKey || readOnly || !config?.found || hasInvalidJson) return;
        setSaving(true);
        try {
            const currentValueType = normalizeValueType(config.valueType);
            if (editValueType !== currentValueType) {
                const metadataResult = await RuntimeConfigurationCommand.UpdateRuntimeConfigurationMetadata(configKey, {
                    displayName: config.displayName || config.key,
                    description: config.description,
                    valueType: editValueType,
                    inputMode: config.inputMode || RuntimeConfigurationInputMode.Auto,
                });

                if (isRuntimeConfigurationOperationFailed(metadataResult)) {
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        text: getRuntimeConfigurationOperationMessage(metadataResult, t('general.error')),
                    });
                    return;
                }
            }

            const result = await RuntimeConfigurationCommand.UpdateRuntimeConfigurationValue(configKey, {
                value: toPayloadValue(editValue, editValueType),
                changeNote: changeNote.trim() || undefined,
            });

            if (isRuntimeConfigurationOperationFailed(result)) {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: getRuntimeConfigurationOperationMessage(result, t('general.error')),
                });
                return;
            }

            setChangeNote('');
            const newVersion = resolveRuntimeConfigurationMutationVersion(result);
            await loadVersions();
            if (newVersion != null) {
                navigateToVersion(newVersion);
            } else if (config?.currentVersion != null) {
                navigateToVersion(config.currentVersion);
            }
            Swal.fire({ icon: 'success', title: t('general.success'), timer: 1500, showConfirmButton: false });
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : t('general.error');
            Swal.fire({ icon: 'error', title: t('general.error'), text: message });
        } finally {
            setSaving(false);
        }
    };

    const handlePublishVersion = async () => {
        if (isViewingCurrentVersion || selectedVersion == null) return;
        setSaving(true);
        try {
            const result = await RuntimeConfigurationCommand.PublishRuntimeConfigurationVersion(configKey, {
                sourceVersion: selectedVersion,
                changeNote: changeNote.trim() || undefined,
            });

            if (isRuntimeConfigurationOperationFailed(result)) {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: getRuntimeConfigurationOperationMessage(result, t('general.error')),
                });
                return;
            }

            setChangeNote('');
            const newVersion = resolveRuntimeConfigurationMutationVersion(result);
            await loadVersions();
            if (newVersion != null) {
                navigateToVersion(newVersion);
            } else if (config?.currentVersion != null) {
                navigateToVersion(config.currentVersion);
            }
            Swal.fire({ icon: 'success', title: t('general.success'), timer: 1500, showConfirmButton: false });
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : t('general.error');
            Swal.fire({ icon: 'error', title: t('general.error'), text: message });
        } finally {
            setSaving(false);
        }
    };

    if (!configKey) {
        return null;
    }

    const displayTitle = config?.found ? config.displayName || configKey : configKey;

    return (
        <SCUIContent>
            <Box className="rc-page rc-page--wide">
                <Box className="rc-page__breadcrumb">
                    <SCUIBreadCrumb
                        onlyTitle={displayTitle}
                        components={[
                            { title: t('runtimeConfiguration.title'), link: PageUrl.runtimeConfiguration.url },
                            { title: displayTitle },
                        ]}
                    />
                </Box>

                <Box className="rc-loading-bar">
                    <SCUILoadingLinear display={loading} />
                </Box>

                {!loading && config && !config.found && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {t(`runtimeConfiguration.notFound.${config.notFoundReason || 'KeyNotFound'}`)}
                    </Alert>
                )}

                {config?.found && (
                    <>
                        <Box className="rc-metadata-card rc-card">
                            <Box className="rc-card__body">
                                <Box className="rc-metadata-top">
                                    <Box>
                                        <Box className="rc-metadata-badges">
                                            <h1 className="rc-metadata-key">{config.key}</h1>
                                        </Box>
                                        <Box className="rc-metadata-badges">
                                            {liveCurrentVersion != null && (
                                                <span className="rc-badge rc-badge--active">
                                                    {t('runtimeConfiguration.detail.activeVersionLabel', {
                                                        version: liveCurrentVersion,
                                                    })}
                                                </span>
                                            )}
                                            {!isViewingCurrentVersion && viewingVersion != null && (
                                                <span className="rc-badge rc-badge--archive">
                                                    {t('runtimeConfiguration.detail.archiveVersionLabel', {
                                                        version: viewingVersion,
                                                    })}
                                                </span>
                                            )}
                                        </Box>
                                        {config.description && (
                                            <p className="rc-metadata-desc">{config.description}</p>
                                        )}
                                    </Box>

                                    <Box className="rc-version-toolbar">
                                        <Box>
                                            <label className="rc-field-label" htmlFor="rc-detail-version">
                                                {t('runtimeConfiguration.detail.viewingVersion')}
                                            </label>
                                            <TextField
                                                id="rc-detail-version"
                                                select
                                                size="small"
                                                value={String(viewingVersion ?? '')}
                                                onChange={(event) => handleVersionChange(event.target.value)}
                                                sx={{ minWidth: 260 }}
                                            >
                                                {versions.map((v) => (
                                                    <MenuItem key={v.version} value={String(v.version)}>
                                                        {v.isCurrent
                                                            ? t('runtimeConfiguration.detail.currentVersionLabel', {
                                                                  version: v.version,
                                                              })
                                                            : t('runtimeConfiguration.detail.versionOptionLabel', {
                                                                  version: v.version,
                                                                  date: new Date(v.createdDate).toLocaleString(),
                                                                  user: v.createdBy || '—',
                                                              })}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Box className="rc-detail-grid">
                            <Box>
                                <Box className="rc-editor-card rc-card">
                                    <Box className="rc-card__header">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CodeIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                            <h3 className="rc-card__header-title">
                                                {t('runtimeConfiguration.detail.editorCardTitle')}
                                            </h3>
                                        </Box>
                                        {!readOnly && (
                                            <span className="rc-editor-card__status rc-editor-card__status--editable">
                                                <FiberManualRecordIcon sx={{ fontSize: 10 }} />
                                                {t('runtimeConfiguration.detail.editableStatus')}
                                            </span>
                                        )}
                                    </Box>
                                    <Box className="rc-card__body">
                                        <Box className="rc-editor-slot">
                                            <RuntimeConfigurationValueTypePicker
                                                value={editValueType}
                                                readOnly={readOnly}
                                                onChange={handleValueTypeChange}
                                            />
                                            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                                {t(`runtimeConfiguration.typeHelp.${editValueType}`)}
                                            </Typography>
                                            <RuntimeConfigurationValueEditor
                                                key={`${config.key}-${editValueType}-${config.currentVersion}-${config.resolvedVersion}`}
                                                valueType={editValueType}
                                                value={editValue}
                                                readOnly={readOnly}
                                                onChange={setEditValue}
                                                onValidationChange={setJsonValidation}
                                            />
                                        </Box>

                                        <Box className="rc-change-note">
                                            <label className="rc-field-label" htmlFor="rc-change-note">
                                                {t('runtimeConfiguration.detail.changeNote')}
                                            </label>
                                            <SCUITextField
                                                id="rc-change-note"
                                                value={changeNote}
                                                placeholder={t('runtimeConfiguration.detail.changeNotePlaceholder')}
                                                helperText={t('runtimeConfiguration.detail.changeNoteHint')}
                                                disabled={loading || saving}
                                                onChange={(textValue: string) => setChangeNote(textValue)}
                                            />
                                        </Box>

                                        <Box className="rc-detail-actions">
                                            <Box className="rc-detail-actions__group">
                                                {isViewingCurrentVersion ? (
                                                    <SCUIButton
                                                        type="primary"
                                                        text={t('runtimeConfiguration.detail.publishNewVersion')}
                                                        showStartIcon
                                                        startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                                                        disabled={saving || loading || hasInvalidJson}
                                                        onClick={handleSave}
                                                    />
                                                ) : (
                                                    <SCUIButton
                                                        type="primary"
                                                        text={t('runtimeConfiguration.detail.publishVersion')}
                                                        showStartIcon
                                                        startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                                                        disabled={saving || loading}
                                                        onClick={handlePublishVersion}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            <Box>
                                <Box className="rc-card">
                                    <Box className="rc-card__header">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <HistoryIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                            <h3 className="rc-card__header-title">
                                                {t('runtimeConfiguration.detail.versionHistory')}
                                            </h3>
                                        </Box>
                                        <span className="rc-version-tag">
                                            {t('runtimeConfiguration.detail.versionCount', { count: versions.length })}
                                        </span>
                                    </Box>
                                    <Box className="rc-card__body">
                                        {versions.length === 0 ? (
                                            <Typography variant="body2" className="rc-empty-state">
                                                {t('runtimeConfiguration.detail.historyEmpty')}
                                            </Typography>
                                        ) : (
                                            <ol className="rc-timeline">
                                                {versions.map((v) => {
                                                    const isViewing = viewingVersion === v.version;
                                                    const cardClass = [
                                                        'rc-timeline__card',
                                                        v.isCurrent ? 'rc-timeline__card--active' : '',
                                                        isViewing && !v.isCurrent ? 'rc-timeline__card--viewing' : '',
                                                        isViewing && v.isCurrent ? 'rc-timeline__card--selected' : '',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ');

                                                    return (
                                                        <li key={v.version} className="rc-timeline__item">
                                                            <span
                                                                className={`rc-timeline__dot ${
                                                                    v.isCurrent ? 'rc-timeline__dot--active' : ''
                                                                }`}
                                                            >
                                                                {v.isCurrent ? (
                                                                    <CheckIcon sx={{ fontSize: 12 }} />
                                                                ) : (
                                                                    <CommitIcon sx={{ fontSize: 12 }} />
                                                                )}
                                                            </span>
                                                            <Box className={cardClass}>
                                                                <Box className="rc-timeline__head">
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <span className="rc-timeline__version">
                                                                            v{v.version}
                                                                        </span>
                                                                        {v.isCurrent && (
                                                                            <span className="rc-badge rc-badge--version">
                                                                                {t('runtimeConfiguration.detail.currentBadge')}
                                                                            </span>
                                                                        )}
                                                                    </Box>
                                                                    <span className="rc-timeline__date">
                                                                        {new Date(v.createdDate).toLocaleString()}
                                                                    </span>
                                                                </Box>
                                                                {v.changeNote && (
                                                                    <p className="rc-timeline__note">{v.changeNote}</p>
                                                                )}
                                                                <Box className="rc-timeline__footer">
                                                                    <span
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: 4,
                                                                        }}
                                                                    >
                                                                        <PersonOutlineIcon sx={{ fontSize: 12 }} />
                                                                        {v.createdBy || '—'}
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        className="rc-timeline__view-btn"
                                                                        onClick={() =>
                                                                            handleVersionChange(String(v.version))
                                                                        }
                                                                    >
                                                                        <span>{t('runtimeConfiguration.detail.inspect')}</span>
                                                                        <ArrowForwardIcon sx={{ fontSize: 12 }} />
                                                                    </button>
                                                                </Box>
                                                            </Box>
                                                        </li>
                                                    );
                                                })}
                                            </ol>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </SCUIContent>
    );
};
